import { TITLE } from '../constants/oneLink';
import { IS_RECEIVER_ADDRESS_FIXED } from '../constants/shipping';
import {
  IOLAdditionalInfo,
  IOLSecurityInfo,
  IOLShippingInfo,
  IOLSummaryInfo,
  TOLInventoryInfo,
  TUpdateOneLinkChunk,
} from '../contexts/OneLinkSummaryProvider';
import { IPromiseCallbacks } from './redux';
import { FilterTypesEnum, IApiError } from './shell';

import { IOrgAddress } from './addressBook';
import { ICampaignItem, ICampaignSummary, IFetchCampaignSummariesResponsePayload } from './campaigns';
import { IBusinessReason } from './reasons';
import { ICommonAddress, ShippingOptionsEnum } from './shipping';
import { IUser } from './users';

//#region OneLink enums

export enum OneLinkStatusEnum {
  Active = 'active',
  NotActive = 'not_active',
}

//#endregion

export interface IOneLinkCreatedSend {
  uid: string;
  first_name: string;
  last_name: string;
  order_id: string;
  tracking_url: string | null;
  digital_gift_url: string | null;
}

export interface IOneLink {
  created_at: string;
  org_id: string;
  pre_created_engagement_id: string;
  department_id: string;
  uid: string;
  shipping_option: ShippingOptionsEnum;
  business_reason_id: string;
  internal_notes: string;
  status: OneLinkStatusEnum;
  disable_shipment_tracking: boolean;
  disable_email_notifications: boolean;
  created_by: string;
  expiration_date: string;
  url: string;
  pick_campaign_ids: string[] | null;
  items: ICampaignItem[] | null;
  user_id: string;
  sender_address1: string;
  sender_address2: string;
  sender_city: string;
  sender_company_name: string;
  sender_country: string;
  sender_email: string;
  sender_first_name: string;
  sender_last_name: string;
  sender_phone: string;
  sender_province: string;
  sender_state: string;
  sender_zip: string;
  send_on_behalf_the_user: {
    avatar_url: string;
    uid: string;
    firstname: string;
    lastname: string;
    email: string;
  };
  note_message: string;
  email_message: string;
  email_subject: string;
  email_template_id: string | null;
  on_hold_until?: string;
  password?: string;
  allowed_domains?: string[];
  max_submit_count?: number;
  enabled_2fa?: boolean;
  recipients_count?: number | null;
  recipients?: undefined;
  receiver_fixed_address?: ICommonAddress;
  [IS_RECEIVER_ADDRESS_FIXED]: boolean;
  org_address_ids?: string[];
  disable_sender_email_notifications: boolean;
  [TITLE]?: string;
  campaign_name?: string;
  use_times_count: number;
  disable_recipient_shipped_email: boolean;
}

export interface IOneLinkWithCampaignSummaries extends IOneLink {
  pick_campaigns?: ICampaignSummary[];
}

export interface IOneLinksState {
  [OneLinkStatusEnum.Active]: { [key: string]: IOneLinkWithCampaignSummaries } | null;
  [OneLinkStatusEnum.NotActive]: { [key: string]: IOneLinkWithCampaignSummaries } | null;
  isLoading: boolean;
  isLoadingSingle: boolean;
  isEditing: boolean;
  error: IApiError | null;
  isFinal: boolean;
  totalCount: number;
  recipients?: IOneLinkRecipient[] | null;
}

export interface IOneLinkSelectorMetadata {
  query?: string;
  filter?: FilterTypesEnum;
  status: OneLinkStatusEnum;
}

export interface IOneLinkSelectorMetadataWithUserId extends IOneLinkSelectorMetadata {
  userId?: string;
}

//#region Request interfaces

export interface IFetchOneLinkSendsRequestPayload extends IPromiseCallbacks {
  page_size: number;
  page: number;
  status?: OneLinkStatusEnum; // Enum. Not required. All if empty
  isOrgSearch?: boolean;
  isDeptSearch?: boolean;
  department_ids?: string[];
  range?: {
    from: string;
    to: string;
  };
  search_query?: string;
}

export interface IFetchOneLinkSendsSuccessPayload {
  one_link_sends: IOneLink[] | null;
  status: OneLinkStatusEnum;
  total_count: number;
  enabled_2fa?: boolean;
  recipients_count?: number;
}

export interface IFetchOneLinkByIdRequestPayload extends IPromiseCallbacks {
  itemId: string;
  status: OneLinkStatusEnum;
  enabled_2fa?: boolean;
  recipients_count?: number;
}

/**
 * Metadata for fetching one link by id
 * @param isSilent - if true, the request will not show a global loading spinner,
 * as well as the response will be saved on top of the existing one link in redux.oneLinks[status] object
 */
export interface IFetchOneLinkByIdRequestMetadata {
  isSilent?: boolean;
}

export interface IFetchOneLinkByIdResponsePayload {
  one_link_send: IOneLink;
}

export interface IFetchOneLinkByIdSuccessPayload extends IFetchOneLinkByIdResponsePayload {
  status: OneLinkStatusEnum;
}

export interface IPopulateOneLinkCampaignSummariesRequestPayload {
  campaign_ids: string[];
  itemId: string;
  status: OneLinkStatusEnum;
}

export interface IPopulateOneLinkCampaignSummariesResponsePayload extends IFetchCampaignSummariesResponsePayload {
  itemId: string;
  status: OneLinkStatusEnum;
}

export type IClearOneLinkCampaignSummaries = Omit<IPopulateOneLinkCampaignSummariesRequestPayload, 'campaign_ids'>;

export interface IDisableOneLinkSendRequestPayload extends IPromiseCallbacks {
  uid: string;
}

export interface IDisableOneLinkSendSuccessPayload {
  uid: string;
}

export interface IActivateOneLinkSendRequestPayload extends IPromiseCallbacks {
  uid: string;
}

export interface IActivateOneLinkSendSuccessPayload {
  uid: string;
}

export interface IOneLinkRecipient {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  is_submitted?: boolean;
}

export interface IOneLinkRecipientWithId extends IOneLinkRecipient {
  uid: string;
  parent_uid: string;
  authorized_at: string;
  parent_id: string;
}

export type TOneLinkRecipient = IOneLinkRecipient &
  // ExistingOneLinkRecipient
  (| {
        uid?: string;
        id?: never;
        error?: never;
      }
    // NewOneLinkRecipient
    | {
        id?: string;
        uid?: never;
        error?: never;
      }
    // InvalidOneLinkRecipient
    | {
        error?: string;
        id?: never;
        uid?: never;
      }
  );

export interface IOneLinkRecipients {
  successfulRecipients?: TOneLinkRecipient[];
  failedRecipients?: TOneLinkRecipient[];
}

export interface IAddRecipientsPayload {
  valid_recipients: IOneLinkRecipientWithId[];
  invalid_recipients: IOneLinkRecipientWithId[];
}

export interface IGetOneLinkRecipientsRequestPayload extends IPromiseCallbacks {
  one_link_id: string;
}

export interface IGetOneLinkRecipientsSuccessPayload {
  data: IOneLinkRecipient[] | null;
}

export interface IEditOneLinkRequestPayload extends IPromiseCallbacks {
  oneLink: { uid: string; status: OneLinkStatusEnum } & TUpdateOneLinkChunk;
}

export interface IEditOneLinkSuccessPayload {
  uid: string;
  status: OneLinkStatusEnum;
  chunk: IOLSummaryInfo | IOLSecurityInfo | IOLAdditionalInfo | IOLShippingInfo | TOLInventoryInfo;
}

//#endregion

export enum OlErrorKeys {
  ExpirationDateOutdated = 'Expiration date is outdated',
  SenderDeleted = 'Sender does not exist',
  OnHoldDateOutdated = 'On Hold date is outdated',
  BusinessReasonRequired = 'Business Reason is required',
  RedemptionLimitLessThanAvailableSends = 'Redemption limit is less than count of available sends',
  OutOfStockItems = 'Out-of-stock items',
  LessThanTwoValidCampaigns = 'Less than 2 valid campaigns',
  InvalidAddresses = 'One or more selected organization addresses are inactive',
  InvalidAddress = 'Receiver address is invalid',
  SomeItemsInvalid = 'Some items are invalid',
  SomeCampaignsNotFound = 'Some campaigns not found',
  SomeCampaignsNotActive = 'Some campaigns are not active',
  PickYourGiftNeedsTwoOrMoreCampaigns = "'Pick your Gift' needs 2 or more selected campaigns",
  EngagementHasBothItemsAndPickYourGiftCampaigns = 'Engagement has both items and "Pick your Gift" campaigns',
  CampaignNotFound = 'Campaign not found',
  CampaignNotActive = 'Campaign is not active',
  BusinessReasonInvalid = 'Business reason is invalid',
  MaxSubmitCountInvalidValue = 'Max submit count has invalid value: %v',
  MaxSubmitCountLessThanOneLinkUsed = 'Max submit count is less than OneLink was used: %v',
  SenderNotFound = 'Sender not found',
  SenderNotFromSameOrganization = 'Sender is not from the same organization',
  ExpirationDateShouldBeInFuture = 'Expiration date should be in the future',
  ReceiverAddressDuplicated = 'Receiver address is duplicated',
  ReceiverAddressMissing = 'Receiver address is missing',
  ErrorHappenedWhileFindingAddresses = 'Error happened while finding addresses',
  SomeAddressesDontBelongToCurrentOrganization = "Some addresses in this campaign don't belong to the current organization",
  OnlyActiveAddressesCanBeAddedToAddressBook = 'Only active addresses can be added to the address book. Please adjust your selection',
}

export interface IOLValidationDependencies {
  users?: IUser[] | null;
  reasons: IBusinessReason[] | null;
  instance: IOneLinkWithCampaignSummaries | null;
  addressBook?: IOrgAddress[] | null;
  isBusinessReasonRequiredForSingleCampaign?: boolean;
}

export enum OneLinkModalEnum {
  None = 'none',
  Error = 'error',
  Confirmation = 'confirmation',
}

export enum OneLinkBannerEnum {
  None = 'none',
  Error = 'error',
}
